/**
 * Persian Loader Hook
 * Custom hook for managing loading states with Persian-inspired loaders
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface LoadingState {
  isLoading: boolean;
  message?: string;
  variant?: 'default' | 'minimal' | 'ornate';
}

export interface UsePersianLoaderReturn {
  loading: LoadingState;
  startLoading: (message?: string, variant?: 'default' | 'minimal' | 'ornate') => void;
  stopLoading: () => void;
  withLoading: <T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    message?: string,
    variant?: 'default' | 'minimal' | 'ornate'
  ) => (...args: T) => Promise<R>;
}

export const usePersianLoader = (initialMessage?: string): UsePersianLoaderReturn => {
  const [loading, setLoading] = useState<LoadingState>({
    isLoading: false,
    message: initialMessage || undefined,
    variant: 'default'
  });

  const timeoutRef = useRef<NodeJS.Timeout>();

  const startLoading = useCallback((message?: string, variant: 'default' | 'minimal' | 'ornate' = 'default') => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setLoading({
      isLoading: true,
      message: message || initialMessage || undefined,
      variant
    });
  }, [initialMessage]);

  const stopLoading = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Add a small delay to prevent flicker, but ensure it always stops
    timeoutRef.current = setTimeout(() => {
      setLoading(prev => ({
        ...prev,
        isLoading: false
      }));
    }, 100); // Short delay to prevent flicker
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const withLoading = useCallback(<T extends any[], R>(
    fn: (...args: T) => Promise<R>,
    message?: string,
    variant: 'default' | 'minimal' | 'ornate' = 'default'
  ) => {
    return async (...args: T): Promise<R> => {
      startLoading(message, variant);
      try {
        const result = await fn(...args);
        return result;
      } finally {
        stopLoading();
      }
    };
  }, [startLoading, stopLoading]);

  return {
    loading,
    startLoading,
    stopLoading,
    withLoading
  };
};

/** i18n keys (common namespace) — pass through t() before showing in PersianOverlayLoader. */
export const LOADING_MESSAGE_KEYS = {
  SIGNING_IN: 'loaders.signingIn',
  SIGNING_UP: 'loaders.signingUp',
  LOADING_DASHBOARD: 'loaders.loadingDashboard',
  SAVING_PROGRESS: 'loaders.savingProgress',
  LOADING_LESSON: 'loaders.loadingLesson',
  PROCESSING_AUDIO: 'loaders.processingAudio',
  GENERATING_QUIZ: 'loaders.generatingQuiz',
  CHECKING_PRONUNCIATION: 'loaders.checkingPronunciation',
  LOADING_VOCABULARY: 'loaders.loadingVocabulary',
  SAVING_SETTINGS: 'loaders.savingSettings',
  LOADING_PROFILE: 'loaders.loadingProfile',
  UPDATING_PROFILE: 'loaders.updatingProfile',
  LOADING_ANALYTICS: 'loaders.loadingAnalytics',
  EXPORTING_DATA: 'loaders.exportingData',
  IMPORTING_DATA: 'loaders.importingData',
  CONNECTING: 'loaders.connecting',
  SYNCHRONIZING: 'loaders.synchronizing',
  VALIDATING: 'loaders.validating',
  PROCESSING: 'loaders.processing',
  LOADING: 'loading',
} as const;

/** @deprecated Use LOADING_MESSAGE_KEYS with t('common:…') instead of hardcoded English. */
export const LOADING_MESSAGES = LOADING_MESSAGE_KEYS;

export default usePersianLoader;